<script lang="ts">
	import { onMount, setContext } from "svelte"
	import { ImageEditor, type Resolution } from "../../modules/ImageEditor"
	import { type MaskRepository } from "../../modules/MaskRepository"
	import { ImageModifierController, type ImageSaveCallback } from "./ImageModifierController.svelte"
	import ImageModifierPreview from "./ImageModifier_Preview.svelte"
	import ImageModifierComplex from "./ImageModifier_Complex.svelte"
	import ImageModifierSimple from "./ImageModifier_Simple.svelte"
	import { Switch, ToggleSwitch } from "misc-ui"
	import { Responsive } from "responsive"

	const CANVAS_CONTAINER_ID = "image-editor-canvas-container"

	interface Props {
		imgSrc: string
		imgRes: Resolution
		maskRepo: MaskRepository
		onSave: ImageSaveCallback
		onCancel: () => void
	}

	let { imgRes, imgSrc, maskRepo, onSave, onCancel }: Props = $props()

	let controller: ImageModifierController = $state()
	let view = $derived(controller?.view ?? "simple")
	let isMounted = $state(false)
	let modifiedImage = $derived(controller?.modifiedImage)

	onMount(() => {
		const imageEditor = new ImageEditor(CANVAS_CONTAINER_ID, imgRes, imgSrc)
		controller = new ImageModifierController(
			imgSrc,
			imgRes,
			imageEditor,
			maskRepo,
			onSave,
			onCancel,
		)

		setContext("controller", controller)

		isMounted = true
	})

	$effect(() => {
		if (view) controller.setImageEditor(new ImageEditor(CANVAS_CONTAINER_ID, imgRes, imgSrc))
	})
</script>

{#if !Responsive.isMobile && !modifiedImage}
	<div class="more-options">
		<div class="switch-container">
			<span class="label">Advanced: </span>
			<ToggleSwitch
				isEnabled={view === "complex"}
				onUpdate={(bool) => (controller.view = bool ? "complex" : "simple")}
			/>
		</div>
	</div>
{/if}
{#if view === "complex"}
	<ImageModifierComplex>
		{#snippet canvas()}
			<div class="image-container transparent-bg">
				<div id={CANVAS_CONTAINER_ID} class="canvas-container"></div>
				{#if isMounted}
					<ImageModifierPreview img={modifiedImage} />
				{/if}
			</div>
		{/snippet}
	</ImageModifierComplex>
{:else if view === "simple"}
	<ImageModifierSimple {controller}>
		{#snippet canvas()}
			<div class="image-container transparent-bg">
				<div id={CANVAS_CONTAINER_ID} class="canvas-container"></div>
				{#if isMounted}
					<ImageModifierPreview img={modifiedImage} />
				{/if}
			</div>
		{/snippet}
	</ImageModifierSimple>
{/if}

<style>
	.image-container {
		height: 100%;
		position: relative;
		min-height: 50vh;
		width: calc(100% - var(--sidebar-width));
		flex-shrink: 1;
	}

	.switch-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.5rem;
	}
	.more-options {
		position: absolute;
		top: 1rem;
		right: 1rem;
		z-index: 1;
	}

	.label {
		color: var(--color-text);
	}

	:global(.isMobile) .image-container {
		width: 100%;
	}

	.canvas-container {
		height: 100%;
		width: 100%;
	}
</style>
